"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MultiSelectField = void 0;
const i18n_1 = require("../../../exports/i18n");
const joi_1 = __importDefault(require("joi"));
const lodash_1 = require("lodash");
const color_1 = require("../../../model/color");
const utils_1 = require("../../../model/utils");
const field_types_1 = require("../../../types/field_types");
const view_types_1 = require("../../../types/view_types");
const utils_2 = require("../../../utils");
const datasheet_1 = require("../../datasheet");
const common_select_field_1 = require("./common_select_field");
class MultiSelectField extends common_select_field_1.SelectField {
    constructor(field, state) {
        super(field, state);
        this.field = field;
        this.state = state;
    }
    static createDefault(fieldMap) {
        return {
            id: datasheet_1.DatasheetActions.getNewFieldId(fieldMap),
            type: field_types_1.FieldType.MultiSelect,
            name: datasheet_1.DatasheetActions.getDefaultFieldName(fieldMap),
            property: this.defaultProperty(),
        };
    }
    validateCellValue(cv) {
        return MultiSelectField.cellValueSchema.validate(cv, { context: { field: this.field } });
    }
    validateOpenWriteValue(owv) {
        return MultiSelectField.openWriteValueSchema.validate(owv, { context: { field: this.field } });
    }
    defaultValue() {
        const defaultValue = this.field.property.defaultValue;
        if (!defaultValue || !defaultValue.length) {
            return null;
        }
        return defaultValue;
    }
    get basicValueType() {
        return field_types_1.BasicValueType.Array;
    }
    get innerBasicValueType() {
        return field_types_1.BasicValueType.String;
    }
    get acceptFilterOperators() {
        return [
            view_types_1.FOperator.Is,
            view_types_1.FOperator.IsNot,
            view_types_1.FOperator.Contains,
            view_types_1.FOperator.DoesNotContain,
            view_types_1.FOperator.IsEmpty,
            view_types_1.FOperator.IsNotEmpty,
            view_types_1.FOperator.IsRepeat,
        ];
    }
    get openValueJsonSchema() {
        return {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    id: {
                        type: 'string',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_option_IDs)
                    },
                    name: {
                        type: 'string',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_option_names),
                    },
                    color: {
                        type: 'object',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_option_colors),
                        properties: {
                            name: {
                                type: 'string',
                                title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_color_names),
                            },
                            value: {
                                type: 'string',
                                title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_color_values),
                            }
                        }
                    },
                },
            }
        };
    }
    isMultiValueField() {
        return true;
    }
    defaultValueForCondition(condition) {
        const { value } = condition;
        if (condition.operator === view_types_1.FOperator.Is) {
            if (!value || value.length === 0) {
                return null;
            }
            const result = value.reduce((prev, v) => {
                if (this.validate([v])) {
                    prev.push(v);
                }
                return prev;
            }, []);
            return result.length === value.length ? result : null;
        }
        if (condition.operator === view_types_1.FOperator.Contains) {
            // `contains` only fills in when there is only one value
            if (value && value.length === 1 && value[0] && this.validate([value[0]])) {
                return [value[0]];
            }
            return null;
        }
        return null;
    }
    compare(cellValue1, cellValue2, orderInCellValueSensitive) {
        // grouping sort
        if (!orderInCellValueSensitive) {
            const sortCellValue1 = this.sortValueByOptionOrder(cellValue1);
            const sortCellValue2 = this.sortValueByOptionOrder(cellValue2);
            return super.compare(sortCellValue1, sortCellValue2);
        }
        return super.compare(cellValue1, cellValue2);
    }
    isMeetFilter(operator, cellValue, conditionValue) {
        switch (operator) {
            case view_types_1.FOperator.Is: {
                return cellValue != null && (0, utils_2.isSameSet)(cellValue, conditionValue);
            }
            case view_types_1.FOperator.IsNot: {
                return cellValue == null || !(0, utils_2.isSameSet)(cellValue, conditionValue);
            }
            case view_types_1.FOperator.Contains: {
                return cellValue != null && (0, utils_2.hasIntersect)(cellValue, conditionValue);
            }
            case view_types_1.FOperator.DoesNotContain: {
                return cellValue == null || !(0, utils_2.hasIntersect)(cellValue, conditionValue);
            }
            default: {
                return super.isMeetFilter(operator, cellValue, conditionValue);
            }
        }
    }
    cellValueToStdValue(val) {
        const stdVal = {
            sourceType: field_types_1.FieldType.MultiSelect,
            data: [],
        };
        if (Array.isArray(val)) {
            stdVal.data = val.map(currOptId => {
                const option = (0, lodash_1.find)(this.field.property.options, opt => {
                    return opt.id === currOptId;
                });
                return {
                    text: option.name,
                    id: option.id,
                };
            });
        }
        return stdVal;
    }
    stdValueToCellValue(stdValue) {
        // filter the empty text
        let data = stdValue.data.filter(d => d.text);
        data = (0, lodash_1.uniqBy)(data, 'text');
        const isSelect2multi = (0, utils_2.isSelectType)(stdValue.sourceType);
        const ids = data.reduce((ids, { text }) => {
            const textTmp = isSelect2multi ? [text] : (0, lodash_1.uniq)(text.split(/, ?/));
            textTmp.forEach(text => {
                const option = this.field.property.options.find(option => option.name === text);
                if (option) {
                    ids.push(option.id);
                }
            });
            return ids;
        }, []);
        return (0, utils_1.handleEmptyCellValue)(ids, this.basicValueType);
    }
    cellValueToString(cellValue) {
        return this.arrayValueToString(this.cellValueToArray(cellValue));
    }
    cellValueToArray(cellValue) {
        if (!cellValue) {
            return null;
        }
        const result = [];
        for (let i = 0, l = cellValue.length; i < l; i++) {
            const option = this.findOptionById(cellValue[i]);
            if (option) {
                result.push(option.name);
            }
        }
        return (0, utils_1.handleEmptyCellValue)(result, this.basicValueType);
    }
    arrayValueToString(cellValues) {
        return cellValues && cellValues.length ? cellValues.join(', ') : null;
    }
    // sort vales by option ascending order
    sortValueByOptionOrder(value) {
        if (!value) {
            return null;
        }
        const mapOptions = this.getMapOption(this.field.property.options);
        return Array.from(value).sort((id1, id2) => {
            const idx1 = mapOptions.get(id1);
            const idx2 = mapOptions.get(id2);
            return idx1 - idx2;
        });
    }
    validate(value) {
        return (0, lodash_1.isArray)(value) && value.every(id => this.field.property.options.some(option => option.id === id));
    }
    cellValueToApiStandardValue(cellValue) {
        return this.cellValueToArray(cellValue);
    }
    cellValueToApiStringValue(cellValue) {
        return this.cellValueToString(cellValue);
    }
    cellValueToOpenValue(cellValue) {
        if (!cellValue) {
            return null;
        }
        const optionMap = this.getMapOption(this.field.property.options);
        return cellValue.reduce((prev, curr) => {
            if (optionMap.has(curr)) {
                const optionIndex = optionMap.get(curr);
                const option = this.field.property.options[optionIndex];
                prev.push({
                    id: option.id,
                    name: option.name,
                    color: (0, color_1.getFieldOptionColor)(option.color)
                });
            }
            return prev;
        }, []);
    }
    openWriteValueToCellValue(openWriteValue) {
        if ((0, utils_1.isNullValue)(openWriteValue)) {
            return null;
        }
        const isSimple = openWriteValue.length && typeof openWriteValue[0] === 'string';
        const writeValue = isSimple ? openWriteValue : openWriteValue.map(v => v.id || v.name);
        const optionIds = [];
        writeValue.forEach(value => {
            this.field.property.options.forEach(option => {
                if ((0, common_select_field_1.isOptionId)(value) && value === option.id) {
                    optionIds.push(option.id);
                }
                else if (value === option.name) {
                    optionIds.push(option.id);
                }
            });
        });
        return optionIds;
    }
    validateUpdateOpenProperty(updateProperty, effectOption) {
        const result = MultiSelectField.openUpdatePropertySchema.validate(updateProperty);
        if (result.error) {
            return result;
        }
        return this.validateWriteOpenOptionsEffect(updateProperty, effectOption);
    }
    filterValueToOpenFilterValue(value) {
        if (Array.isArray(value)) {
            const _value = value.filter(v => this.findOptionById(v));
            return _value.length ? _value : null;
        }
        return value;
    }
    openFilterValueToFilterValue(value) {
        if (Array.isArray(value)) {
            const _value = value.filter(v => this.findOptionById(v));
            return _value.length ? _value : null;
        }
        return value;
    }
    validateOpenFilterValue(value) {
        return MultiSelectField.validateOpenFilterSchema.validate(value);
    }
}
exports.MultiSelectField = MultiSelectField;
MultiSelectField.cellValueSchema = joi_1.default.custom((optionIds, helpers) => {
    var _a;
    const field = (_a = helpers.prefs['context']) === null || _a === void 0 ? void 0 : _a['field'];
    if (!(0, lodash_1.isArray)(optionIds)) {
        return helpers.message({ en: 'cellValue is not array' });
    }
    if (!optionIds.every(id => field.property.options.some(option => option.id === id))) {
        return helpers.message({ en: 'option not exist field property' });
    }
    return optionIds;
}).allow(null).required();
MultiSelectField.openWriteValueSchema = joi_1.default.array().custom((owv, helpers) => {
    var _a;
    const field = (_a = helpers.prefs['context']) === null || _a === void 0 ? void 0 : _a['field'];
    const optionIdsOrNames = owv.map((v) => (0, lodash_1.isString)(v) ? v : (v.id || v.name));
    if (!optionIdsOrNames) {
        return helpers.error('value format error');
    }
    if (field.property.options.every(option => option.id === optionIdsOrNames || option.name === optionIdsOrNames)) {
        return helpers.error('option not exist field property');
    }
    return optionIdsOrNames;
}).allow(null).required();
MultiSelectField.openUpdatePropertySchema = joi_1.default.object({
    options: joi_1.default.array().items(joi_1.default.object({
        id: joi_1.default.string(),
        name: joi_1.default.string().required(),
        color: joi_1.default.string(),
    })).required(),
    defaultValue: joi_1.default.array().items(joi_1.default.string())
}).required();
MultiSelectField.validateOpenFilterSchema = joi_1.default.array().items(joi_1.default.string()).allow(null);
//# sourceMappingURL=multi_select_field.js.map